import Globals from "./Globals";
import { NeoTypes } from "./AppServices";
import { DataType } from "./Model/Misc";
export var NumberFormat;
(function (NumberFormat) {
    // Mask is [Thousands Separator, Decimals (2 places), Percent, Currency Symbol]
    NumberFormat[NumberFormat["None"] = 0] = "None";
    NumberFormat[NumberFormat["NoDecimals"] = 4096] = "NoDecimals";
    NumberFormat[NumberFormat["Decimals"] = 4352] = "Decimals";
    NumberFormat[NumberFormat["PercentNoDecimals"] = 4112] = "PercentNoDecimals";
    NumberFormat[NumberFormat["PercentDecimals"] = 4368] = "PercentDecimals";
    NumberFormat[NumberFormat["CurrencyNoDecimals"] = 4097] = "CurrencyNoDecimals";
    NumberFormat[NumberFormat["CurrencyDecimals"] = 4353] = "CurrencyDecimals";
})(NumberFormat || (NumberFormat = {}));
var NumberUtils = /** @class */ (function () {
    function NumberUtils() {
    }
    Object.defineProperty(NumberUtils, "decimalService", {
        /** Decimal service used internally by neo. Project code should use the chosen decimal package directly. */
        get: function () {
            if (!this._decimalService) {
                this._decimalService = Globals.appService.get(NeoTypes.Core.DecimalService);
            }
            return this._decimalService;
        },
        enumerable: false,
        configurable: true
    });
    NumberUtils.initialise = function () {
        NumberUtils.numberFormats[NumberFormat.None] = "#";
        NumberUtils.numberFormats[NumberFormat.NoDecimals] = "#,##0;(#,##0)";
        NumberUtils.numberFormats[NumberFormat.Decimals] = "#,##0.00;(#,##0.00)";
        NumberUtils.numberFormats[NumberFormat.PercentNoDecimals] = "#,##0%;(#,##0)%";
        NumberUtils.numberFormats[NumberFormat.PercentDecimals] = "#,##0.00%;(#,##0.00%)";
        NumberUtils.numberFormats[NumberFormat.CurrencyNoDecimals] = "_#,##0;(_#,##0)";
        NumberUtils.numberFormats[NumberFormat.CurrencyDecimals] = "_#,##0.00;(_#,##0.00)";
    };
    /** Uses the users local decimal separator when formatting and parsing numbers. */
    NumberUtils.useLocaleDecimalSeparator = function () {
        this.decimalSeparator = 1.1.toLocaleString().substring(1, 2);
        if (this.decimalSeparator !== ".") {
            this.allowAsDecimalSeparator = this.decimalSeparator;
        }
    };
    /** Gets a format string for the pre-defined number format.
     * @param displayDecimals No of decimals to display if using a decimals format.
    */
    NumberUtils.getFormatString = function (format, currencySymbol, displayDecimals) {
        if (displayDecimals === void 0) { displayDecimals = 2; }
        var formatString = NumberUtils.numberFormats[format];
        if ((format & 0x0100) === 0x0100) {
            if (displayDecimals === 0) {
                formatString = formatString.replace(/.00/g, "");
            }
            else if (displayDecimals === 1) {
                formatString = formatString.replace(/.00/g, ".0");
            }
            else if (displayDecimals > 2) {
                formatString = formatString.replace(/.00/g, ".00" + "#".repeat(displayDecimals - 2));
            }
        }
        return formatString.replace(/_/g, currencySymbol || NumberUtils.defaultCurrencySymbol);
    };
    /**
     * Formats a number into a string that can be parsed back to a number. E.g. without currency symbol or group separators
     * @param number js number
     * @param format Format string including group separators / currency symbol. This is used to get the number of decimal to display.
     * @param hasExternalPercentSymbol True if this is a percent, but the format string doesn't contain a % symbol.
     * @param maxDecimals Max no of decimals supported by the value. For percentages this should be 2 more than the no of displayed decimals.
     */
    NumberUtils.formatRaw = function (number, format, hasExternalPercentSymbol, maxDecimals) {
        var parts = format.split(";");
        var isPercent = format.indexOf("%") >= 0 || hasExternalPercentSymbol;
        if (isPercent && NumberUtils.transformPercent) {
            number *= 100;
            isPercent = false; //Don't allow extra precision below.
            if (maxDecimals !== undefined) {
                maxDecimals -= 2;
            }
        }
        var positive = "#0" + NumberUtils.getDecimalsFormatPart(parts[0], isPercent, maxDecimals), negative = "-#0" + NumberUtils.getDecimalsFormatPart(parts[1] ? parts[1] : parts[0], isPercent, maxDecimals);
        return NumberUtils.format(number, positive + ";" + negative);
    };
    /**
     * Rounds a number, getting the precision from the format string
     * @param hasExternalPercentSymbol True if this is a percent, but the format string doesn't contain a % symbol.
     */
    NumberUtils.roundUsingFormat = function (number, formatString, hasExternalPercentSymbol) {
        var decimals = this.getDecimalPlaces(formatString, number);
        if (formatString.indexOf("%") >= 0 || hasExternalPercentSymbol) {
            decimals += 2;
        }
        return this.round(number, decimals);
    };
    /**
     * Rounds a number to the nearest no of decimals.
     */
    NumberUtils.round = function (number, decimals) {
        if (this.decimalService.isDecimal(number)) {
            // Round away from zero.
            return this.decimalService.round(number, decimals);
        }
        else {
            if (decimals > 0) {
                return parseFloat(number.toFixed(decimals));
            }
            else {
                return parseInt(number.toFixed(0));
            }
        }
    };
    /**
     * Returns the number of decimal places in a format string.
     * @param formatString Format string.
     * @param value Value if the negative format string is different to positive.
     */
    NumberUtils.getDecimalPlaces = function (formatString, value) {
        var parts = formatString.split(";");
        var part = value === undefined || value >= 0 || parts.length == 1 ? parts[0] : parts[1];
        var startIndex = part.indexOf(".");
        var endIndex = Math.max(part.lastIndexOf("0"), part.lastIndexOf("#"));
        if (startIndex >= 0 && endIndex > 0) {
            return endIndex - startIndex;
        }
        return 0;
    };
    NumberUtils.getDecimalsFormatPart = function (formatString, isPercent, maxDecimals) {
        if (isPercent === void 0) { isPercent = false; }
        var decimalsPart = "";
        var startIndex = formatString.indexOf(".");
        var endIndex = Math.max(formatString.lastIndexOf("0"), formatString.lastIndexOf("#"));
        if (startIndex >= 0 && endIndex > 0) {
            decimalsPart = formatString.substring(startIndex, endIndex + 1) + (isPercent ? "##" : "");
        }
        else {
            decimalsPart = isPercent ? ".##" : "";
        }
        if (maxDecimals !== undefined && maxDecimals > 0) {
            // If the format string has less decimals than maxDecimals, then add # characters on the end.
            if (!decimalsPart) {
                decimalsPart = ".";
            }
            if (decimalsPart.length - 1 < maxDecimals) {
                decimalsPart += "#".repeat(maxDecimals - (decimalsPart.length - 1));
            }
        }
        return decimalsPart;
    };
    NumberUtils.format = function (value, format, currencySymbol) {
        if (this.decimalService.isDecimal(value)) {
            value = this.decimalService.toNumber(value);
        }
        if (value !== null && typeof value === "object") {
            var numberValue = this.decimalService.toNumber(value.value);
            return NumberUtils.formatUsingEnum(numberValue, value.propertyInfo.propertyType === DataType.Float || value.propertyInfo.propertyType === DataType.Decimal ? NumberFormat.Decimals : NumberFormat.NoDecimals);
        }
        else if (typeof format === "string") {
            return NumberUtils.formatInternal(value, format);
        }
        else {
            return NumberUtils.formatUsingEnum(value, format, currencySymbol);
        }
    };
    NumberUtils.formatUsingEnum = function (number, format, currencySymbol) {
        return NumberUtils.formatInternal(number, NumberUtils.getFormatString(format, currencySymbol));
    };
    NumberUtils.formatInternal = function (number, formatString) {
        if (number === undefined) {
            // Developer has made a mistake somewhere, let them know.
            return "undefined";
        }
        if (number === null) {
            // Developer has made a mistake somewhere, let them know.
            return "null";
        }
        if (!formatString || isNaN(number)) {
            return number.toString(); //return as it is.
        }
        var isNegative = number < 0;
        number = Math.abs(number);
        formatString = formatString.replace(/ /g, '\xA0'); //replace spaces with non-breaking spaces.
        //Check if the format string is a 2 part string
        var negSeparatorIndex = formatString.indexOf(';');
        if (negSeparatorIndex > 0) {
            if (isNegative) {
                //use the negative part
                formatString = formatString.substr(negSeparatorIndex + 1);
            }
            else {
                //use the positive part
                formatString = formatString.substr(0, negSeparatorIndex);
            }
        }
        var fmtStart = formatString.indexOf('#');
        var fmtEnd = Math.max(formatString.lastIndexOf('#'), formatString.lastIndexOf('0'));
        var beforeText = '', afterText = '';
        if (fmtStart > 0) {
            beforeText = formatString.substr(0, fmtStart);
        }
        if (fmtEnd < formatString.length - 1) {
            afterText = formatString.substr(fmtEnd + 1);
        }
        formatString = formatString.substr(fmtStart, fmtEnd - fmtStart + 1);
        //search for separator for group & decimal, anything not digit, not +/- sign, not #.
        var formatChars = formatString.match(/[^\d\-\+#]/g);
        var decimal = (formatChars && formatChars[formatChars.length - 1]) || '.'; //treat the right most symbol as decimal 
        var group = (formatChars && formatChars[1] && formatChars[0]) || ','; //treat the left most symbol as group separator
        if (decimal == group)
            decimal = '.';
        //split the decimal for the format string if any.
        var numberParts = formatString.split(decimal);
        //Check if this is a percent
        number = afterText.indexOf('%') < 0 ? number : number * 100;
        //Fix the decimal first, toFixed will auto fill trailing zero.
        var numberString = number.toFixed(numberParts[1] ? numberParts[1].length : 0);
        numberString = +(numberString) + ''; //convert number to string to trim off *all* trailing decimal zero(es)
        //fill back any trailing zero according to format
        var pos_trail_zero = numberParts[1] ? numberParts[1].lastIndexOf('0') : 0; //look for last zero in format
        var part = numberString.split('.');
        //integer will get !part[1]
        if (!part[1] || part[1] && part[1].length <= pos_trail_zero) {
            numberString = (+numberString).toFixed(pos_trail_zero + 1);
        }
        var szSep = numberParts[0].split(group); //look for separator
        numberParts[0] = szSep.join(''); //join back without separator for counting the pos of any leading 0.
        var pos_lead_zero = numberParts[0] ? numberParts[0].indexOf('0') : 0;
        if (pos_lead_zero > -1) {
            while (part[0].length < (numberParts[0].length - pos_lead_zero)) {
                part[0] = '0' + part[0];
            }
        }
        else if (+part[0] == 0) {
            part[0] = '';
        }
        var numberStrings = numberString.split('.');
        numberStrings[0] = part[0];
        //process the first group separator from decimal (.) only, the rest ignore.
        //get the length of the last slice of split result.
        var pos_separator = (szSep[1] && szSep[szSep.length - 1].length);
        if (pos_separator) {
            var integer = numberStrings[0];
            var str = '';
            var offset = integer.length % pos_separator;
            for (var i = 0, l = integer.length; i < l; i++) {
                str += integer.charAt(i); //ie6 only support charAt for sz.
                //-pos_separator so that won't trail separator on full length
                if (!((i - offset + 1) % pos_separator) && i < l - pos_separator) {
                    str += group;
                }
            }
            numberStrings[0] = str;
        }
        if (this.decimalSeparator) {
            decimal = this.decimalSeparator;
        }
        numberStrings[1] = (numberParts[1] && numberStrings[1]) ? decimal + numberStrings[1] : "";
        numberStrings[0] = numberStrings[0].replace(/,/g, NumberUtils.thousandsSeparator);
        return beforeText + numberStrings[0] + numberStrings[1] + afterText; //put back any negation and combine integer and fraction.
    };
    ;
    NumberUtils.thousandsSeparator = "\xA0"; // non breaking space.
    NumberUtils.defaultCurrencySymbol = "R";
    NumberUtils.numberFormats = {};
    /** If values typed into numeric editors should be divided by 100 when stored. */
    NumberUtils.transformPercent = true;
    return NumberUtils;
}());
export default NumberUtils;
NumberUtils.initialise();
